import { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * A custom hook to replace the Prompt component from react-router-dom v5
 * @param {boolean} when - Whether the prompt should be shown
 * @param {string|function} message - The message to show in the prompt
 */
export const usePrompt = (when, message) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;

  // Handle browser beforeunload event (closing tab, refreshing page)
  useEffect(() => {
    if (when) {
      const handleBeforeUnload = (event) => {
        const msg =
          typeof message === 'function' ? message({ pathname: currentPath }) : message;
        if (msg !== true) {
          event.preventDefault();
          event.returnValue = msg;
          return msg;
        }
        return undefined;
      };

      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
    return undefined;
  }, [when, message, currentPath]);

  // Create a custom navigation handler
  const customNavigate = useCallback(
    (to, options) => {
      if (!when) {
        navigate(to, options);
        return;
      }

      const msg = typeof message === 'function' ? message({ pathname: to }) : message;
      if (msg === true || window.confirm(msg)) {
        navigate(to, options);
      }
    },
    [when, message, navigate],
  );

  // Override the window history methods to intercept navigation
  useEffect(() => {
    if (!when) return undefined;

    // Save original methods
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    // Override pushState
    window.history.pushState = function (state, title, url) {
      const msg = typeof message === 'function' ? message({ pathname: url }) : message;
      if (msg === true || window.confirm(msg)) {
        return originalPushState.apply(this, [state, title, url]);
      }
      return undefined;
    };

    // override replaceState
    window.history.replaceState = function (state, title, url) {
      const msg = typeof message === 'function' ? message({ pathname: url }) : message;
      if (msg === true || window.confirm(msg)) {
        return originalReplaceState.apply(this, [state, title, url]);
      }
      return undefined;
    };

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [when, message]);

  // return the custom navigate function for use in components
  return customNavigate;
};
